import { useState } from "react";
import { ArrowLeft, ChevronDown, ChevronUp, Building2, ShoppingCart, GraduationCap, HeartHandshake } from "lucide-react";
import { submitHotelForm, startMembershipCheckout, fetchHotelPlans, startAddonCheckout, isHotelModeActive, getCachedHotel } from "./hotelClient";
import OrgVerification from "./OrgVerification";
import { useEffect } from "react";

const SECTOR_META = {
  hotel:   { label: "Hotel",   icon: Building2,      accent: "#B89823", tiles: "Hotels & Kids' Clubs" },
  school:  { label: "School",  icon: GraduationCap,  accent: "#5A87B8", tiles: "Schools" },
  charity: { label: "Charity", icon: HeartHandshake, accent: "#B85A87", tiles: "Charities" },
};

// All user-facing strings for the sign-up + add-on flows. Each sector uses its
// own copy so words like "iPad", "Kids' Club", "HTL-", "Hotel Mode" only
// appear on the Hotel screens and are correctly swapped for Schools and
// Charities. Keep this table as the single source of truth.
const SECTOR_COPY = {
  hotel: {
    subheader: "kids' club, iPad and venue",
    defaultSharedLabel: "Main Kids' Club",
    welcomePlaceholder: "e.g. The Grand Ocean Hotel",
    welcomeHelper: "Enter the hotel name exactly as you would like children and families to see it.",
    locationsLabel: "Number of kids' clubs, venues or buildings at this address",
    choiceADesc: "The same code activates every kids' club or building at this address.",
    choiceBDesc: "One code per kids' club/building. All codes still share the same central credit balance.",
    sharedLabelField: "Shared venue label",
    sharedLabelPlaceholder: "e.g. Main Kids' Club",
    devicesLabel: "Expected number of iPads / devices",
    devicesHelper: "This becomes the maximum number of simultaneously active iPads. One code activates all of them and every device shares the same central credit balance.",
    devicesShortLabel: "iPads",
    venueLabelPrefix: "Venue",
    venuePlaceholder: (i) => `e.g. ${i === 0 ? "Main Hotel Kids' Club" : i === 1 ? "Beach Kids' Club" : "West Wing Playroom"}`,
    separateHelper: "One code per venue, and each code's iPad number becomes its maximum active-device limit. All venues share the same central credit balance.",
    paymentFooter: "Your hotel account and activation codes are created after Stripe confirms payment. The codes and setup instructions are emailed to the manager email.",
    exampleOneLine: "One kids' club: one shared code. All iPads share the same credit balance.",
    exampleThreeLine: "Three locations: either one code shared across all three, OR three labelled codes (Main Hotel Kids' Club, Beach Kids' Club, West Wing Playroom) — all still share the same central credit balance.",
    exampleOneHead: "One kids' club:",
    exampleThreeHead: "Three locations:",
    addonsHeading: "Already a hotel member? Add more chat credits",
    addonsDesc: "Top up your shared central credit balance — Add-on credits never expire. An active hotel membership is required to use them.",
    verifyPanel: "Verify your hotel",
    verifyEmailLabel: "Registered hotel contact email",
    verifyEmailPlaceholder: "manager@yourhotel.com",
    codePlaceholder: "HTL-XXXX-XXXX",
    verifyErrBoth: "Please enter both your hotel contact email and your HTL-XXXX-XXXX activation code.",
    verifySecurity: "Both fields are required for security — email alone is not enough. If this device is already in Hotel Mode, we'll skip this step automatically.",
    modeName: "Hotel Mode",
    activeBanner: "This iPad is already in Hotel Mode — add-on credits will apply to your hotel's shared balance automatically.",
    addonNameStrip: "Hotel Add-On",
  },
  school: {
    subheader: "classroom, room and device",
    defaultSharedLabel: "Main Classroom",
    welcomePlaceholder: "e.g. Sunnyside Primary School",
    welcomeHelper: "Enter the school name exactly as you would like children and families to see it.",
    locationsLabel: "Number of classrooms, rooms or buildings at this address",
    choiceADesc: "The same code activates every classroom, room or building at this address.",
    choiceBDesc: "One code per classroom, room or building. All codes still share the same central credit balance.",
    sharedLabelField: "Shared classroom / room label",
    sharedLabelPlaceholder: "Main Classroom",
    devicesLabel: "Expected number of school devices",
    devicesHelper: "This becomes the maximum number of simultaneously active school devices. One code activates all of them and every device shares the same central credit balance.",
    devicesShortLabel: "Devices",
    venueLabelPrefix: "Classroom",
    venuePlaceholder: (i) => `e.g. ${i === 0 ? "Main Classroom" : i === 1 ? "SEND Room" : "Library"}`,
    separateHelper: "One code per classroom or room, and each code's device number becomes its maximum active-device limit. All classrooms share the same central credit balance.",
    paymentFooter: "Your school account and activation codes are created after Stripe confirms payment. The codes and setup instructions are emailed to the school contact email.",
    exampleOneLine: "One shared code activates every school device.",
    exampleThreeLine: "Three classrooms: either one code shared across all three, OR three labelled codes (Main Classroom, SEND Room, Library) — all still share the same central credit balance.",
    exampleOneHead: "One classroom:",
    exampleThreeHead: "Three classrooms:",
    addonsHeading: "Already a school member? Add more chat credits",
    addonsDesc: "Top up your shared central credit balance — add-on credits never expire. An active school membership is required to use them.",
    verifyPanel: "Verify your school",
    verifyEmailLabel: "Registered school contact email",
    verifyEmailPlaceholder: "office@yourschool.sch.uk",
    codePlaceholder: "SCH-XXXX-XXXX",
    verifyErrBoth: "Please enter both your school contact email and your SCH-XXXX-XXXX activation code.",
    verifySecurity: "Both fields are required for security — email alone is not enough. If this device is already in School Mode, we'll skip this step automatically.",
    modeName: "School Mode",
    activeBanner: "This device is already in School Mode — add-on credits will apply to your school's shared balance automatically.",
    addonNameStrip: "School Add-On",
  },
  charity: {
    subheader: "site, room and device",
    defaultSharedLabel: "Main Activity Room",
    welcomePlaceholder: "e.g. Rainbow Kids Charity",
    welcomeHelper: "Enter the charity name exactly as you would like children and families to see it.",
    locationsLabel: "Number of sites, rooms or buildings at this address",
    choiceADesc: "The same code activates every site, room or building at this address.",
    choiceBDesc: "One code per site, room or building. All codes still share the same central credit balance.",
    sharedLabelField: "Shared site / room label",
    sharedLabelPlaceholder: "Main Activity Room",
    devicesLabel: "Expected number of charity devices",
    devicesHelper: "This becomes the maximum number of simultaneously active charity devices. One code activates all of them and every device shares the same central credit balance.",
    devicesShortLabel: "Devices",
    venueLabelPrefix: "Site",
    venuePlaceholder: (i) => `e.g. ${i === 0 ? "Main Activity Room" : i === 1 ? "Family Room" : "Community Hall"}`,
    separateHelper: "One code per site or room, and each code's device number becomes its maximum active-device limit. All sites share the same central credit balance.",
    paymentFooter: "Your charity account and activation codes are created after Stripe confirms payment. The codes and setup instructions are emailed to the charity contact email.",
    exampleOneLine: "One shared code activates every charity device.",
    exampleThreeLine: "Three sites: either one code shared across all three, OR three labelled codes (Main Activity Room, Family Room, Community Hall) — all still share the same central credit balance.",
    exampleOneHead: "One site:",
    exampleThreeHead: "Three sites:",
    addonsHeading: "Already a charity member? Add more chat credits",
    addonsDesc: "Top up your shared central credit balance — add-on credits never expire. An active charity membership is required to use them.",
    verifyPanel: "Verify your charity",
    verifyEmailLabel: "Registered charity contact email",
    verifyEmailPlaceholder: "contact@yourcharity.org.uk",
    codePlaceholder: "CHR-XXXX-XXXX",
    verifyErrBoth: "Please enter both your charity contact email and your CHR-XXXX-XXXX activation code.",
    verifySecurity: "Both fields are required for security — email alone is not enough. If this device is already in Charity Mode, we'll skip this step automatically.",
    modeName: "Charity Mode",
    activeBanner: "This device is already in Charity Mode — add-on credits will apply to your charity's shared balance automatically.",
    addonNameStrip: "Charity Add-On",
  },
};

// Reused for hotels, schools and charities. Everything sector-specific comes
// from the `sector` prop (default: hotel for backwards-compat with existing
// /hotel/signup URL).
export default function HotelSignUp({ onBack, sector = "hotel" }) {
  const meta = SECTOR_META[sector] || SECTOR_META.hotel;
  const copy = SECTOR_COPY[sector] || SECTOR_COPY.hotel;
  const [plans, setPlans] = useState(null);
  const [tierId, setTierId] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [hotelName, setHotelName] = useState("");
  const [welcomeName, setWelcomeName] = useState("");
  const [address, setAddress] = useState("");
  const [country, setCountry] = useState("");
  const [managerName, setManagerName] = useState("");
  const [managerEmail, setManagerEmail] = useState("");
  const [numLocations, setNumLocations] = useState(1);
  const [arrangement, setArrangement] = useState("shared"); // shared | separate
  const [sharedLabel, setSharedLabel] = useState(copy.defaultSharedLabel);
  const [sharedDeviceCount, setSharedDeviceCount] = useState("");
  const [separateLabels, setSeparateLabels] = useState([copy.defaultSharedLabel]);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [separateDeviceCounts, setSeparateDeviceCounts] = useState([""]);
  const [showExamples, setShowExamples] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchHotelPlans(sector).then((p) => {
      setPlans(p);
      if (p?.memberships?.length && !tierId) setTierId(p.memberships[0].price_id);
    }).catch(() => setPlans({ memberships: [], addons: [] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sector]);

  // If the visitor clicked "Buy add-on credits" on the previous page, scroll
  // straight to the add-ons block instead of the top of the setup form.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash === "#addons") {
      // wait until plans arrive so the section has full height
      const t = setTimeout(() => {
        const el = document.getElementById("addons");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [plans]);

  // Keep separateLabels + counts in sync with numLocations
  useEffect(() => {
    setSeparateLabels((prev) => {
      const next = [...prev];
      while (next.length < numLocations) next.push(`${copy.venueLabelPrefix} ${next.length + 1}`);
      next.length = numLocations;
      return next;
    });
    setSeparateDeviceCounts((prev) => {
      const next = [...prev];
      while (next.length < numLocations) next.push("");
      next.length = numLocations;
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numLocations]);

  const parseCount = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };

  const canSubmit = () => {
    if (!verifyToken) return false;
    if (!tierId) return false;
    if (!hotelName.trim() || !address.trim() || !country.trim()) return false;
    if (!welcomeName.trim() || welcomeName.trim().length < 2 || welcomeName.trim().length > 60) return false;
    if (/[<>{}\\]|https?:\/\/|www\./i.test(welcomeName)) return false;
    if (!managerName.trim() || !managerEmail.trim()) return false;
    if (numLocations < 1) return false;
    if (arrangement === "shared") {
      if (!sharedLabel.trim()) return false;
      const n = parseCount(sharedDeviceCount);
      return n !== null && n >= 1 && n <= 200;
    }
    if (separateLabels.length !== numLocations || separateLabels.some((v) => !v.trim())) return false;
    if (separateDeviceCounts.length !== numLocations) return false;
    return separateDeviceCounts.every((v) => {
      const n = parseCount(v);
      return n !== null && n >= 1 && n <= 200;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) {
      setErr("Please fill every field before continuing.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const body = {
        sector,
        verify_token: verifyToken,
        hotel_name: hotelName,
        welcome_name: welcomeName,
        hotel_address: address,
        country,
        manager_name: managerName,
        manager_email: managerEmail,
        num_locations: numLocations,
        code_arrangement: arrangement,
        venue_labels: arrangement === "shared" ? [sharedLabel] : separateLabels,
        venue_device_counts: arrangement === "shared" ? [parseCount(sharedDeviceCount)] : separateDeviceCounts.map(parseCount),
        membership_price_id: tierId,
        marketing_consent: marketingConsent,
      };
      const { pending_id } = await submitHotelForm(body);
      const { url } = await startMembershipCheckout(pending_id);
      window.location.href = url;
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E0E0E0] px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={onBack} className="p-2 rounded-full hover:bg-[#F3F0E6] transition-colors" data-testid="hotel-signup-back">
            <ArrowLeft className="w-6 h-6 text-[#4A4A4A]" />
          </button>
          <h1 className="text-lg sm:text-xl font-semibold text-[#4A4A4A]">{meta.label} Membership</h1>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-6 sm:py-10">
        <div className="text-center mb-6">
          <meta.icon className="w-9 h-9 mx-auto mb-2" style={{ color: meta.accent }} />
          <h2 className="text-2xl sm:text-3xl font-bold text-[#4A4A4A]">Set up your {meta.label.toLowerCase()}</h2>
          <p className="text-sm text-[#6B6B6B] mt-1">One shared credit balance across every {copy.subheader} at your {meta.label.toLowerCase()}.</p>
        </div>

        {/* --- ORG VERIFICATION (anti-fraud gate) --- */}
        <div className="mb-5">
          <OrgVerification
            sector={sector}
            sectorLabel={meta.label}
            onVerified={(tok, email) => { setVerifyToken(tok); if (email) { setVerifiedEmail(email); setManagerEmail(email); } }}
          />
        </div>

        <button
          type="button"
          onClick={() => setShowExamples((v) => !v)}
          data-testid="hotel-signup-examples-toggle"
          className="text-xs text-[#B89823] hover:text-[#8A7620] mb-4 inline-flex items-center gap-1"
        >
          {showExamples ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {showExamples ? "Hide examples" : "How activation codes work — examples"}
        </button>
        {showExamples && (
          <div className="bg-[#FFF7D6] border border-[#E8D4A8] rounded-2xl p-4 mb-6 text-xs text-[#5A4A32] space-y-2" data-testid="hotel-signup-examples">
            <div><strong>{copy.exampleOneHead}</strong> {copy.exampleOneLine}</div>
            <div><strong>{copy.exampleThreeHead}</strong> {copy.exampleThreeLine}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={`space-y-5 ${verifyToken ? "" : "opacity-50 pointer-events-none select-none"}`} data-testid="hotel-signup-form">
          {/* Tier */}
          <div className="bg-white rounded-2xl border-2 border-[#E8E5DC] p-4">
            <label className="block text-xs font-semibold uppercase tracking-wide text-[#4A4A4A] mb-2">Membership tier</label>
            <div className="space-y-2" data-testid="hotel-signup-tiers">
              {(plans?.memberships || []).map((m) => (
                <button
                  type="button"
                  key={m.price_id}
                  data-testid={`hotel-tier-${m.tier}`}
                  onClick={() => setTierId(m.price_id)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all text-left ${tierId === m.price_id ? "border-[#B89823] bg-[#FFF7D6]" : "border-[#E0E0E0] hover:border-[#D9C599]"}`}
                >
                  <div>
                    <p className="font-semibold text-[#4A4A4A]">{m.name}</p>
                    <p className="text-xs text-[#6B6B6B]">{(m.monthly_credits || 0).toLocaleString()} typed/spoken chat credits included every month</p>
                  </div>
                  <p className="font-bold text-[#B89823]">£{m.monthly_price_gbp}/mo</p>
                </button>
              ))}
              {plans && (plans.memberships || []).length === 0 && (
                <p className="text-xs text-[#9A9A9A]" data-testid="hotel-signup-tiers-empty">
                  {meta.label} membership tiers will appear here as soon as pricing is finalised.
                </p>
              )}
              {!plans && <p className="text-xs text-[#9A9A9A]">Loading tiers…</p>}
            </div>
          </div>

          {/* Organisation details */}
          <div className="bg-white rounded-2xl border-2 border-[#E8E5DC] p-4 space-y-3">
            <Field label={`${meta.label} name`} value={hotelName} onChange={setHotelName} testid="hotel-signup-name" required />
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[#4A4A4A] mb-1">Welcome to which {meta.label.toLowerCase()}? *</label>
              <input
                value={welcomeName}
                onChange={(e) => setWelcomeName(e.target.value)}
                maxLength={60}
                placeholder={copy.welcomePlaceholder}
                data-testid="hotel-signup-welcome-name"
                className="w-full px-3 py-2 rounded-xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-sm"
              />
              <p className="text-[11px] text-[#6B6B6B] mt-1">{copy.welcomeHelper}</p>
              {welcomeName.trim() && !/[<>{}\\]|https?:\/\/|www\./i.test(welcomeName) && (
                <div className="mt-2 bg-[#FFF7D6] border border-[#E8D4A8] rounded-xl px-3 py-2 text-sm text-[#4A4A4A]" data-testid="hotel-signup-welcome-preview">
                  Preview: <span className="font-semibold">Welcome to {welcomeName.trim()}!</span>
                </div>
              )}
            </div>
            <Field label={`Full ${meta.label.toLowerCase()} address`} value={address} onChange={setAddress} testid="hotel-signup-address" required textarea />
            <Field label="Country" value={country} onChange={setCountry} testid="hotel-signup-country" required />
            <Field label={`${meta.label} manager / contact name`} value={managerName} onChange={setManagerName} testid="hotel-signup-manager-name" required />
            <Field
              label={`${meta.label} manager / contact email`}
              value={managerEmail}
              onChange={setManagerEmail}
              testid="hotel-signup-manager-email"
              required
              type="email"
              disabled={!!verifiedEmail}
              helper={verifiedEmail ? "Locked to the verified organisation email." : undefined}
            />
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[#4A4A4A] mb-1">{copy.locationsLabel}</label>
              <input
                type="number"
                min={1}
                max={50}
                value={numLocations}
                onChange={(e) => setNumLocations(Math.max(1, Math.min(50, parseInt(e.target.value || "1", 10))))}
                data-testid="hotel-signup-num-locations"
                className="w-32 px-3 py-2 rounded-xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-sm"
              />
            </div>
          </div>

          {/* Arrangement */}
          <div className="bg-white rounded-2xl border-2 border-[#E8E5DC] p-4">
            <label className="block text-xs font-semibold uppercase tracking-wide text-[#4A4A4A] mb-2">Activation-code arrangement</label>
            <div className="space-y-2">
              <button type="button" onClick={() => setArrangement("shared")}
                data-testid="hotel-arrangement-shared"
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${arrangement === "shared" ? "border-[#B89823] bg-[#FFF7D6]" : "border-[#E0E0E0]"}`}>
                <p className="font-semibold text-sm text-[#4A4A4A]">Choice A — One shared code</p>
                <p className="text-xs text-[#6B6B6B]">{copy.choiceADesc}</p>
              </button>
              <button type="button" onClick={() => setArrangement("separate")}
                data-testid="hotel-arrangement-separate"
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${arrangement === "separate" ? "border-[#B89823] bg-[#FFF7D6]" : "border-[#E0E0E0]"}`}>
                <p className="font-semibold text-sm text-[#4A4A4A]">Choice B — Separate labelled codes</p>
                <p className="text-xs text-[#6B6B6B]">{copy.choiceBDesc}</p>
              </button>
            </div>

            {arrangement === "shared" && (
              <div className="mt-3 space-y-3">
                <Field
                  label={copy.sharedLabelField}
                  value={sharedLabel}
                  onChange={setSharedLabel}
                  testid="hotel-signup-shared-label"
                  required
                  placeholder={copy.sharedLabelPlaceholder}
                />
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-[#4A4A4A] mb-1">{copy.devicesLabel} *</label>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    value={sharedDeviceCount}
                    onChange={(e) => setSharedDeviceCount(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="e.g. 6"
                    data-testid="hotel-signup-shared-devices"
                    className="w-32 px-3 py-2 rounded-xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-sm"
                  />
                  <p className="text-[11px] text-[#6B6B6B] mt-1">{copy.devicesHelper}</p>
                </div>
              </div>
            )}
            {arrangement === "separate" && (
              <div className="mt-3 space-y-3">
                {separateLabels.map((v, i) => (
                  <div key={i} className="grid grid-cols-[1fr_120px] gap-2 items-end">
                    <Field
                      label={`${copy.venueLabelPrefix} ${i + 1} label`}
                      value={v}
                      onChange={(val) => setSeparateLabels((prev) => prev.map((p, idx) => idx === i ? val : p))}
                      testid={`hotel-signup-venue-label-${i}`}
                      required
                      placeholder={copy.venuePlaceholder(i)}
                    />
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wide text-[#4A4A4A] mb-1">{copy.devicesShortLabel} *</label>
                      <input
                        type="number"
                        min={1}
                        max={200}
                        value={separateDeviceCounts[i] ?? ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, "");
                          setSeparateDeviceCounts((prev) => prev.map((p, idx) => idx === i ? val : p));
                        }}
                        placeholder="e.g. 6"
                        data-testid={`hotel-signup-venue-devices-${i}`}
                        className="w-full px-3 py-2 rounded-xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-sm"
                      />
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-[#6B6B6B]">{copy.separateHelper}</p>
              </div>
            )}
          </div>

          {err && <div className="bg-[#FFF0F0] border border-[#F5D0D0] rounded-xl p-3 text-sm text-[#A55B64] text-center" data-testid="hotel-signup-error">{err}</div>}

          <label className="flex items-start gap-2 cursor-pointer select-none text-xs leading-relaxed text-[#4A4A4A] px-1" data-testid="hotel-signup-marketing-consent-label">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              data-testid="hotel-signup-marketing-consent"
              className="mt-0.5 accent-[#B89823] w-4 h-4"
            />
            <span>I would like to receive Poodle Talk news, product updates and resources by email. I can unsubscribe at any time.</span>
          </label>

          <button
            type="submit"
            disabled={busy || !canSubmit()}
            data-testid="hotel-signup-submit"
            className="w-full px-4 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold hover:bg-[#D9C599] transition-all disabled:opacity-50"
          >
            {busy ? "Redirecting to Stripe…" : "Continue to payment"}
          </button>
          <p className="text-xs text-[#9A9A9A] text-center">{copy.paymentFooter}</p>
        </form>

        {/* Add-ons section — for orgs that already have a membership. */}
        <div id="addons"><HotelAddonsSection plans={plans} sector={sector} copy={copy} /></div>
      </main>
    </div>
  );
}

function HotelAddonsSection({ plans, sector, copy }) {
  // Only treat this device as "already active" for the CURRENT sector — a
  // hotel device viewing the school add-on page must still see the verify
  // box, otherwise its HTL token would silently attach to a school add-on.
  const cachedSector = getCachedHotel()?.sector;
  const alreadyActive = isHotelModeActive() && cachedSector === sector;
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const handleBuy = async (priceId) => {
    setErr("");
    // If not already in this sector's Mode we require BOTH email + code
    // (spec: email alone is never proof of membership).
    if (!alreadyActive) {
      if (!email.trim() || !code.trim()) {
        setErr(copy.verifyErrBoth);
        return;
      }
    }
    setBusy(priceId);
    try {
      const { url } = await startAddonCheckout(priceId, alreadyActive ? {} : { manager_email: email, code });
      window.location.href = url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start add-on checkout.");
      setBusy("");
    }
  };

  return (
    <section className="mt-12 border-t border-[#E8E5DC] pt-8" data-testid="hotel-addons-section">
      <div className="text-center mb-5">
        <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-[#B89823]" />
        <h2 className="text-xl sm:text-2xl font-bold text-[#4A4A4A]">{copy.addonsHeading}</h2>
        <p className="text-xs text-[#6B6B6B] mt-1">{copy.addonsDesc}</p>
      </div>

      {!alreadyActive && (
        <div className="bg-white rounded-2xl border-2 border-[#E8E5DC] p-4 space-y-3 mb-4" data-testid="hotel-addons-verify-box">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#4A4A4A]">{copy.verifyPanel}</p>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#4A4A4A] mb-1">{copy.verifyEmailLabel} *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={copy.verifyEmailPlaceholder}
              data-testid="hotel-addons-email"
              className="w-full px-3 py-2 rounded-xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#4A4A4A] mb-1">Your activation code *</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={13}
              placeholder={copy.codePlaceholder}
              data-testid="hotel-addons-code"
              className="w-full px-3 py-2 rounded-xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-sm font-mono tracking-wider"
            />
          </div>
          <p className="text-[11px] text-[#6B6B6B]">{copy.verifySecurity}</p>
        </div>
      )}
      {alreadyActive && (
        <div className="bg-[#FFF7D6] border-2 border-[#E8D4A8] rounded-2xl p-3 text-xs text-[#5A4A32] text-center mb-4" data-testid="hotel-addons-active-note">
          {copy.activeBanner}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(plans?.addons || []).map((a) => (
          <div key={a.price_id} className="bg-white rounded-2xl border-2 border-[#E8E5DC] p-5">
            <p className="text-center text-xs font-bold text-[#6B6B6B] uppercase tracking-wide mb-2">{a.name.replace(copy.addonNameStrip, "Add-On")}</p>
            <p className="text-center text-3xl font-bold text-[#4A4A4A]">{(a.credits || 0).toLocaleString()}</p>
            <p className="text-center text-xs text-[#6B6B6B] mb-3">additional credits</p>
            <p className="text-center text-xl font-bold text-[#B89823] mb-3">£{a.price_gbp}</p>
            <button
              onClick={() => handleBuy(a.price_id)}
              disabled={!!busy}
              data-testid={`hotel-addons-buy-${a.credits}`}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold text-sm hover:bg-[#D9C599] transition-all disabled:opacity-50"
            >
              {busy === a.price_id ? "Redirecting…" : (<><ShoppingCart className="w-4 h-4" /> Buy add-on</>)}
            </button>
          </div>
        ))}
        {plans && (plans.addons || []).length === 0 && (
          <p className="text-xs text-[#9A9A9A] col-span-full text-center" data-testid="hotel-addons-empty">
            Add-on options will appear here as soon as pricing is finalised.
          </p>
        )}
        {!plans && <p className="text-xs text-[#9A9A9A]">Loading add-ons…</p>}
      </div>

      {err && <div className="bg-[#FFF0F0] border border-[#F5D0D0] rounded-xl p-3 text-sm text-[#A55B64] text-center mt-4" data-testid="hotel-addons-error">{err}</div>}
    </section>
  );
}

// (Field helper unchanged below)

function Field({ label, value, onChange, testid, required, type = "text", placeholder, textarea, disabled, helper }) {
  const cls = `w-full px-3 py-2 rounded-xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-sm ${disabled ? "bg-[#F3F0E6] text-[#6B6B6B]" : ""}`;
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[#4A4A4A] mb-1">{label}{required ? " *" : ""}</label>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={2} className={cls} placeholder={placeholder} disabled={disabled} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} type={type} className={cls} placeholder={placeholder} disabled={disabled} readOnly={disabled} />
      )}
      {helper && <p className="text-[11px] text-[#6B6B6B] mt-1">{helper}</p>}
    </div>
  );
}
